import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import WmsFullScreenLayout from '../shared/WmsFullScreenLayout';
import useWmsSession from '../shared/useWmsSession';
import WmsScreenAccessGate from '../shared/WmsScreenAccessGate';
import { screenService } from '../services/screenService';
import receivingService, { ReceivingSessionDto, ReceivingItemDto } from '../services/receivingService';
import ReceivingReady from './receiving/ReceivingReady';
import ReceivingScanning from './receiving/ReceivingScanning';
import ReceivingPutaway from './receiving/ReceivingPutaway';
import ReceivingCompleted from './receiving/ReceivingCompleted';

type ReceivingPhase = 'ready' | 'scanning' | 'putaway' | 'completed';

const DEFAULT_RECEIVING_PERMISSIONS = {
  canPicking: false,
  canPacking: false,
  canReceiving: true,
  canCycleCount: false,
  pickingScreenId: null,
  packingScreenId: null,
  receivingScreenId: null,
  cycleCountScreenId: null,
};

const WmsReceivingScreen: React.FC = () => {
  const { screenId } = useParams<{ screenId: string }>();
  const { session, logout } = useWmsSession();

  const [phase, setPhase] = useState<ReceivingPhase>('ready');
  const [warehouseId, setWarehouseId] = useState('');
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [currentSession, setCurrentSession] = useState<ReceivingSessionDto | null>(null);
  const [items, setItems] = useState<ReceivingItemDto[]>([]);
  const [putawayItem, setPutawayItem] = useState<ReceivingItemDto | undefined>();
  const [bulkPutawayItems, setBulkPutawayItems] = useState<ReceivingItemDto[] | undefined>();
  const [pendingBarcode, setPendingBarcode] = useState<string | null>(null);

  useEffect(() => {
    if (!screenId) return;
    (async () => {
      try {
        const res = await screenService.getScreenDetail('receiving', screenId);
        const data = res.data?.data || res.data;
        if (data?.warehouseId) setWarehouseId(data.warehouseId);
      } catch { /* screen not found */ }
      setLoadingScreen(false);
    })();
  }, [screenId]);

  const handleLogout = useCallback(() => {
    logout();
    setCurrentSession(null);
    setItems([]);
    setPhase('ready');
  }, [logout]);

  const handleSessionStart = useCallback((sess: ReceivingSessionDto) => {
    setCurrentSession(sess);
    setItems(sess.items || []);
    setPhase('scanning');
  }, []);

  const handleSessionResume = useCallback(async (sess: ReceivingSessionDto) => {
    try {
      const detail = await receivingService.getSessionDetail(warehouseId, sess.id);
      setCurrentSession(detail);
      setItems(detail.items || []);
    } catch {
      setCurrentSession(sess);
      setItems([]);
    }
    setPhase('scanning');
  }, [warehouseId]);

  const handleQuickStart = useCallback((sess: ReceivingSessionDto, barcode: string) => {
    setCurrentSession(sess);
    setItems([]);
    setPendingBarcode(barcode);
    setPhase('scanning');
  }, []);

  const handlePutawaySingle = useCallback((item: ReceivingItemDto) => {
    setPutawayItem(item);
    setBulkPutawayItems(undefined);
    setPhase('putaway');
  }, []);

  const handleBulkPutaway = useCallback(() => {
    const unplaced = items.filter(i => i.status !== 'PutAway');
    setBulkPutawayItems(unplaced);
    setPutawayItem(undefined);
    setPhase('putaway');
  }, [items]);

  const handlePutawayDone = useCallback((updatedItems: ReceivingItemDto[]) => {
    setItems(prev => {
      const updatedMap = new Map(updatedItems.map(u => [u.id, u]));
      return prev.map(i => updatedMap.get(i.id) || i);
    });
    setPutawayItem(undefined);
    setBulkPutawayItems(undefined);
    setPhase('scanning');
  }, []);

  const handleComplete = useCallback(async () => {
    if (!currentSession) return;
    try {
      await receivingService.completeSession(currentSession.id);
      setPhase('completed');
    } catch (err) {
      try {
        const detail = await receivingService.getSessionDetail(warehouseId, currentSession.id);
        setItems(detail.items || []);
      } catch {  }
      throw err;
    }
  }, [currentSession, warehouseId]);

  const handleCancel = useCallback(async (cancelReason?: string) => {
    if (!currentSession) return;
    // İptali mal kabulü yapan personel gerçekleştirir → oturumdaki cashierName gönderilir.
    await receivingService.cancelSession(currentSession.id, session?.cashierName, cancelReason);
    setCurrentSession(null);
    setItems([]);
    setPutawayItem(undefined);
    setBulkPutawayItems(undefined);
    setPhase('ready');
  }, [currentSession, session]);

  const handleNewSession = useCallback(() => {
    setCurrentSession(null);
    setItems([]);
    setPutawayItem(undefined);
    setBulkPutawayItems(undefined);
    setPhase('ready');
  }, []);

  const content = loadingScreen ? (
    <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fb' }}>
      <CircularProgress />
    </Box>
  ) : (
    <WmsFullScreenLayout
      currentScreen="receiving"
      cashierName={session?.cashierName || ''}
      wmsPermissions={session?.wmsPermissions || DEFAULT_RECEIVING_PERMISSIONS}
      onLogout={handleLogout}
    >
      {phase === 'ready' && (
        <ReceivingReady
          cashierName={session?.cashierName || ''}
          warehouseId={warehouseId}
          onSessionStart={handleSessionStart}
          onSessionResume={handleSessionResume}
          onQuickStart={handleQuickStart}
        />
      )}

      {phase === 'scanning' && currentSession && (
        <ReceivingScanning
          session={currentSession}
          warehouseId={warehouseId}
          items={items}
          onItemsChange={setItems}
          onPutaway={handlePutawaySingle}
          onBulkPutaway={handleBulkPutaway}
          onComplete={handleComplete}
          onCancel={handleCancel}
          quickStartBarcode={pendingBarcode}
          onQuickStartConsumed={() => setPendingBarcode(null)}
        />
      )}

      {phase === 'putaway' && currentSession && (
        <ReceivingPutaway
          sessionId={currentSession.id}
          warehouseId={warehouseId}
          item={putawayItem}
          bulkItems={bulkPutawayItems}
          onDone={handlePutawayDone}
          onBack={() => setPhase('scanning')}
        />
      )}

      {phase === 'completed' && currentSession && (
        <ReceivingCompleted
          sessionNumber={currentSession.sessionNumber}
          items={items}
          startedAt={currentSession.startedAt}
          onNewSession={handleNewSession}
          onLogout={handleLogout}
        />
      )}
    </WmsFullScreenLayout>
  );

  return (
    <WmsScreenAccessGate screenType="receiving" screenId={screenId || ''}>
      {content}
    </WmsScreenAccessGate>
  );
};

export default WmsReceivingScreen;
